<script lang="ts" setup generic="F extends Form<any> = Form">
/**
 * @component ProForm
 * @description @qin-ui/antd-vue-pro 配置驱动表单组件
 *
 * 架构设计说明 (Architecture Overview)
 *
 * [ 数据层 (Core) ]  =>  [ 绑定层 (ProForm) ]  =>  [ 渲染层 (BaseFormItem / BaseField) ]
 *
 * 1. 数据隔离：ProForm 本身不负责存储和管理表单状态，所有核心状态（formData, fields, 校验规则）
 *    均交由底层的 `useForm`（@qin-ui/pro-components-core）驱动。
 * 2. 上下文透传：通过 `provide` 将核心状态下发给子组件，避免了深层嵌套的 Props 传递。
 * 3. 递归渲染：网格布局和具体字段的解析交由 `<BaseFormItem>` 递归完成，实现了 UI 布局与表单逻辑的解耦。
 *
 * @public
 *
 * 通过配置驱动的方式快速构建表单，支持：
 * - 字段联动（通过 setField、watch 等实现）
 * - 嵌套字段（通过 fields 递归配置）
 * - 自定义组件（通过 component 属性和 custom 类型）
 * - 网格布局（通过 grid 属性）
 * - 动态插槽（以字段 path 命名的插槽）
 * - 全局配置注入（通过 ProComponentProvider）
 *
 * @template F - 表单实例类型
 *
 * @param {F} [form] - useForm 返回的表单实例
 * @param {boolean | GridProps} [grid=false] - 是否启用网格布局
 * @param {...FormProps} ...attrs - Ant Design Vue Form 组件的其他属性
 *
 * @slot default - 默认插槽，可用于添加额外内容
 * @slot [path] - 以字段 path 命名的动态插槽，可用于自定义字段渲染
 *
 * @example
 * ```vue
 * <template>
 *   <ProForm :form="form" @submit="handleSubmit">
 *     <template #name="{ value, disabled }">
 *       <a-input :value="value" :disabled="disabled" />
 *     </template>
 *   </ProForm>
 * </template>
 * ```
 */
import { Form as AForm, FormProps as AFormProps } from '../../../../shared/ui';
import { computed, inject, mergeProps, provide, toValue, type Slot } from 'vue';
import { INJECT_CONFIG } from '../../../component-provider/constants';
import { BaseFormItem } from '..';
import { InjectionFormKey, InjectionFormSlotsKey } from '../../constants';
import type { Grid, VModelProps, PathProps } from '../../types';
import { Path, camelizeProperties } from '../../../../shared/core';
import { useForm, type Form } from '../../hooks/useForm';

defineOptions({ name: 'ProForm', inheritAttrs: false });

type MyFormProps = Partial<Omit<AFormProps, 'model'>>;

type Props = { grid?: Grid; form?: F } & /* @vue-ignore */ MyFormProps;
const props = withDefaults(defineProps<Props>(), {
  grid: false,
  form: undefined,
});

/**
 * 依赖注入中心：
 * 若外部未传 form 实例，则自动初始化内部表单实例；
 * 将 Form 实例注入到 Vue Context 中，供深层子组件（如 BaseFormItem, BaseField, 自定义插槽）跨层级直接访问数据和方法。
 */
const fallbackForm = props.form ? undefined : useForm();
const currentForm = computed(
  () => props.form ?? (fallbackForm as unknown as F)
);

/**
 * 表单实例代理：操作全部转发至 currentForm 当前实例；
 * 下游持有本代理即可，:form 运行期替换无需重绑。
 */
const formProxy = new Proxy({} as F, {
  get(_, prop) {
    const target = currentForm.value as any;
    const val = target?.[prop];
    if (typeof val === 'function') {
      return (...args: any[]) => (currentForm.value as any)?.[prop]?.(...args);
    }
    return val;
  },
  set(_, prop, value) {
    if (currentForm.value) {
      (currentForm.value as any)[prop] = value;
    }
    return true;
  },
  has(_, prop) {
    return Boolean(currentForm.value && prop in currentForm.value);
  },
});

provide(InjectionFormKey, formProxy);

const fields = computed(() => {
  const f = currentForm.value?.fields;
  return (Array.isArray(f) ? f : f?.value) ?? [];
});
const formData = computed(() => currentForm.value?.formData);
const setFormRef = (el: any) => currentForm.value?.setFormRef?.(el);

let formInstanceCounter = 0;
const formInstanceKeyMap = new WeakMap<object, number>();
const getFormKey = (f: object) => {
  if (!formInstanceKeyMap.has(f)) {
    formInstanceKeyMap.set(f, ++formInstanceCounter);
  }
  return formInstanceKeyMap.get(f)!;
};
const formKey = computed(() => getFormKey(currentForm.value as any));

const config = INJECT_CONFIG['pro-form'];
const injectFormConfig = inject(config.injectionKey, config.default);

/**
 * 从 ProComponentProvider 获取全局配置，响应式派生以支持动态配置变更
 */
const injectAttrs = computed(() => {
  const raw = toValue(injectFormConfig);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { grid: _injectGrid, ...rest } = raw || {};
  return rest;
});

type ExtractPath<T> = T extends Form<infer D> ? Path<D> : string;
type FieldSlotProps = VModelProps &
  PathProps & { disabled?: boolean; [x: string]: any };
type FieldSlots = Record<ExtractPath<F>, Slot<FieldSlotProps>>;

const slots = defineSlots<Partial<FieldSlots & { default: Slot }>>();

/**
 * 动态插槽映射中心 (Slot Teleporting)：
 * 在 setup 同步期直接提供响应式 slots 上下文容器，供深层 BaseField 按需动态获取插槽
 */
provide(InjectionFormSlotsKey, slots);
</script>

<template>
  <AForm
    :key="formKey"
    :ref="setFormRef"
    :model="formData"
    v-bind="mergeProps(injectAttrs, camelizeProperties($attrs))"
    class="pro-form"
  >
    <BaseFormItem :fields="fields" :grid="props.grid" />
    <slot />
  </AForm>
</template>
